/**
 * Contact validation utilities for the frontend.
 * Provides email and phone validation functions using libphonenumber-js.
 */

import {
  parsePhoneNumber,
  CountryCode as LibCountryCode,
} from 'libphonenumber-js';

// RFC 5322 compliant email regex (simplified but covers most cases)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Validates an email address format.
 */
export function isValidEmail(email: string | undefined | null): boolean {
  if (!email || email.trim() === '') {
    return false;
  }
  
  const trimmed = email.trim().toLowerCase();
  
  // Basic length check (max 254 characters per RFC 5321)
  if (trimmed.length > 254) {
    return false;
  }
  
  return EMAIL_REGEX.test(trimmed);
}

/**
 * Validates an email and returns an error message if invalid.
 */
export function validateEmail(email: string | undefined | null, fieldName = 'Email'): string | null {
  if (!email || email.trim() === '') {
    return `${fieldName} is required`;
  }
  
  if (!isValidEmail(email)) {
    return `Please enter a valid ${fieldName.toLowerCase()} address`;
  }
  
  return null;
}

/**
 * Validates an optional email and returns an error message if provided but invalid.
 */
export function validateOptionalEmail(email: string | undefined | null, fieldName = 'Email'): string | null {
  if (!email || email.trim() === '') {
    return null; // Optional, so empty is OK
  }
  
  if (!isValidEmail(email)) {
    return `Please enter a valid ${fieldName.toLowerCase()} address`;
  }
  
  return null;
}

/**
 * Normalizes email by trimming and lowercasing.
 */
export function normalizeEmail(email: string | undefined | null): string {
  if (!email) return '';
  return email.trim().toLowerCase();
}

/**
 * Map of dial codes to ISO country codes.
 * Used for libphonenumber-js validation.
 */
const DIAL_CODE_TO_COUNTRY: Record<string, LibCountryCode> = {
  '+1': 'US',
  '+7': 'RU',
  '+20': 'EG',
  '+27': 'ZA',
  '+30': 'GR',
  '+31': 'NL',
  '+32': 'BE',
  '+33': 'FR',
  '+34': 'ES',
  '+39': 'IT',
  '+41': 'CH',
  '+43': 'AT',
  '+44': 'GB',
  '+45': 'DK',
  '+46': 'SE',
  '+47': 'NO',
  '+48': 'PL',
  '+49': 'DE',
  '+52': 'MX',
  '+55': 'BR',
  '+60': 'MY',
  '+61': 'AU',
  '+62': 'ID',
  '+63': 'PH',
  '+64': 'NZ',
  '+65': 'SG',
  '+66': 'TH',
  '+81': 'JP',
  '+82': 'KR',
  '+84': 'VN',
  '+86': 'CN',
  '+90': 'TR',
  '+91': 'IN',
  '+92': 'PK',
  '+94': 'LK',
  '+234': 'NG',
  '+254': 'KE',
  '+351': 'PT',
  '+353': 'IE',
  '+358': 'FI',
  '+852': 'HK',
  '+880': 'BD',
  '+886': 'TW',
  '+966': 'SA',
  '+971': 'AE',
  '+972': 'IL',
  '+977': 'NP',
};

/**
 * Gets the ISO country code from a dial code.
 */
export function getCountryFromDialCode(dialCode: string): LibCountryCode {
  const normalized = normalizeCountryCode(dialCode);
  return DIAL_CODE_TO_COUNTRY[normalized] || 'IN';
}

/**
 * Validates a phone number using libphonenumber-js.
 * Only validates mobile numbers that can receive SMS.
 */
export function isValidPhoneNumber(phoneNumber: string | undefined | null, dialCode = '+91'): boolean {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return false;
  }
  
  try {
    const country = getCountryFromDialCode(dialCode);
    const fullNumber = normalizeCountryCode(dialCode) + phoneNumber.trim().replace(/\D/g, '');
    
    // Parse and validate
    const parsed = parsePhoneNumber(fullNumber, country);
    if (!parsed || !parsed.isValid()) {
      return false;
    }
    
    // Only allow MOBILE type (can receive SMS)
    const type = parsed.getType();
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
  } catch {
    return false;
  }
}

/**
 * Validates any phone number (mobile or landline) using libphonenumber-js.
 * Use this for contact information fields where landlines are acceptable.
 */
export function isValidContactPhoneNumber(phoneNumber: string | undefined | null, dialCode = '+91'): boolean {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return false;
  }
  
  try {
    const country = getCountryFromDialCode(dialCode);
    const fullNumber = normalizeCountryCode(dialCode) + phoneNumber.trim().replace(/\D/g, '');
    
    // Parse and validate
    const parsed = parsePhoneNumber(fullNumber, country);
    return parsed !== undefined && parsed.isValid();
  } catch {
    return false;
  }
}

/**
 * Validates a phone number and returns an error message if invalid.
 * Only accepts mobile numbers that can receive SMS.
 */
export function validatePhoneNumber(
  phoneNumber: string | undefined | null, 
  dialCode = '+91', 
  fieldName = 'Phone number'
): string | null {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return `${fieldName} is required`;
  }
  
  try {
    const country = getCountryFromDialCode(dialCode);
    const fullNumber = normalizeCountryCode(dialCode) + phoneNumber.trim().replace(/\D/g, '');
    
    const parsed = parsePhoneNumber(fullNumber, country);
    if (!parsed) {
      return `Please enter a valid ${fieldName.toLowerCase()}`;
    }
    
    if (!parsed.isValid()) {
      return `Please enter a valid ${fieldName.toLowerCase()} for ${dialCode}`;
    }
    
    // Check if it's a mobile number (can receive SMS)
    // Allow UNDEFINED type as well since some valid mobile numbers may not be typed correctly
    const type = parsed.getType();
    if (type !== 'MOBILE' && type !== 'FIXED_LINE_OR_MOBILE' && type !== undefined) {
      return `Please enter a mobile number that can receive SMS`;
    }
    
    return null;
  } catch {
    return `Please enter a valid ${fieldName.toLowerCase()}`;
  }
}

/**
 * Validates a contact phone number (mobile or landline) and returns an error message if invalid.
 * Use this for contact information fields where landlines are acceptable.
 */
export function validateContactPhoneNumber(
  phoneNumber: string | undefined | null, 
  dialCode = '+91', 
  fieldName = 'Phone number'
): string | null {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return `${fieldName} is required`;
  }
  
  try {
    const country = getCountryFromDialCode(dialCode);
    const fullNumber = normalizeCountryCode(dialCode) + phoneNumber.trim().replace(/\D/g, '');
    
    const parsed = parsePhoneNumber(fullNumber, country);
    if (!parsed) {
      return `Please enter a valid ${fieldName.toLowerCase()}`;
    }
    
    if (!parsed.isValid()) {
      return `Please enter a valid ${fieldName.toLowerCase()} for ${dialCode}`;
    }
    
    return null;
  } catch {
    return `Please enter a valid ${fieldName.toLowerCase()}`;
  }
}

/**
 * Validates an optional phone number and returns an error message if provided but invalid.
 * Only accepts mobile numbers that can receive SMS.
 */
export function validateOptionalPhoneNumber(
  phoneNumber: string | undefined | null, 
  dialCode = '+91', 
  fieldName = 'Phone number'
): string | null {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return null; // Optional, so empty is OK
  }
  
  return validatePhoneNumber(phoneNumber, dialCode, fieldName);
}

/**
 * Validates an optional contact phone number (mobile or landline).
 * Use this for contact information fields where landlines are acceptable.
 */
export function validateOptionalContactPhoneNumber(
  phoneNumber: string | undefined | null, 
  dialCode = '+91', 
  fieldName = 'Phone number'
): string | null {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return null; // Optional, so empty is OK
  }
  
  return validateContactPhoneNumber(phoneNumber, dialCode, fieldName);
}

/**
 * Normalizes a phone number by removing non-digit characters.
 */
export function normalizePhoneNumber(phoneNumber: string | undefined | null): string {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\D/g, '');
}

/**
 * Formats a phone number for display using libphonenumber-js.
 */
export function formatPhoneNumber(phoneNumber: string | undefined | null, dialCode = '+91'): string {
  if (!phoneNumber) return '';
  
  try {
    const country = getCountryFromDialCode(dialCode);
    const fullNumber = normalizeCountryCode(dialCode) + phoneNumber.trim().replace(/\D/g, '');
    const parsed = parsePhoneNumber(fullNumber, country);
    
    if (parsed) {
      return parsed.formatInternational();
    }
  } catch {
    // Fall through to default
  }
  
  // Default: just add dial code
  const clean = normalizePhoneNumber(phoneNumber);
  return `${normalizeCountryCode(dialCode)} ${clean}`;
}

/**
 * Normalizes country code to include + prefix.
 */
export function normalizeCountryCode(countryCode: string | undefined | null): string {
  if (!countryCode || countryCode.trim() === '') {
    return '+91'; // Default to India
  }
  
  let code = countryCode.trim();
  
  // Remove leading zeros
  code = code.replace(/^0+/, '');
  
  // Add + if missing
  if (!code.startsWith('+')) {
    code = '+' + code;
  }
  
  return code;
}

/**
 * Country codes list with search-friendly data.
 */
export interface CountryCode {
  code: string;
  dialCode: string;
  name: string;
  flag: string;
  searchTerms: string; // For search functionality
}

export const COUNTRY_CODES: CountryCode[] = [
  { code: 'IN', dialCode: '+91', name: 'India', flag: '🇮🇳', searchTerms: 'india in +91 91' },
  { code: 'US', dialCode: '+1', name: 'United States', flag: '🇺🇸', searchTerms: 'usa us united states america +1 1' },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom', flag: '🇬🇧', searchTerms: 'uk gb united kingdom britain england +44 44' },
  { code: 'AU', dialCode: '+61', name: 'Australia', flag: '🇦🇺', searchTerms: 'australia au +61 61' },
  { code: 'CA', dialCode: '+1', name: 'Canada', flag: '🇨🇦', searchTerms: 'canada ca +1 1' },
  { code: 'DE', dialCode: '+49', name: 'Germany', flag: '🇩🇪', searchTerms: 'germany de deutschland +49 49' },
  { code: 'FR', dialCode: '+33', name: 'France', flag: '🇫🇷', searchTerms: 'france fr +33 33' },
  { code: 'IT', dialCode: '+39', name: 'Italy', flag: '🇮🇹', searchTerms: 'italy it italia +39 39' },
  { code: 'ES', dialCode: '+34', name: 'Spain', flag: '🇪🇸', searchTerms: 'spain es españa +34 34' },
  { code: 'JP', dialCode: '+81', name: 'Japan', flag: '🇯🇵', searchTerms: 'japan jp nihon +81 81' },
  { code: 'CN', dialCode: '+86', name: 'China', flag: '🇨🇳', searchTerms: 'china cn zhongguo +86 86' },
  { code: 'KR', dialCode: '+82', name: 'South Korea', flag: '🇰🇷', searchTerms: 'korea kr south korea +82 82' },
  { code: 'SG', dialCode: '+65', name: 'Singapore', flag: '🇸🇬', searchTerms: 'singapore sg +65 65' },
  { code: 'AE', dialCode: '+971', name: 'UAE', flag: '🇦🇪', searchTerms: 'uae ae dubai united arab emirates +971 971' },
  { code: 'SA', dialCode: '+966', name: 'Saudi Arabia', flag: '🇸🇦', searchTerms: 'saudi arabia sa +966 966' },
  { code: 'BR', dialCode: '+55', name: 'Brazil', flag: '🇧🇷', searchTerms: 'brazil br brasil +55 55' },
  { code: 'MX', dialCode: '+52', name: 'Mexico', flag: '🇲🇽', searchTerms: 'mexico mx +52 52' },
  { code: 'RU', dialCode: '+7', name: 'Russia', flag: '🇷🇺', searchTerms: 'russia ru +7 7' },
  { code: 'ZA', dialCode: '+27', name: 'South Africa', flag: '🇿🇦', searchTerms: 'south africa za +27 27' },
  { code: 'NZ', dialCode: '+64', name: 'New Zealand', flag: '🇳🇿', searchTerms: 'new zealand nz +64 64' },
  { code: 'MY', dialCode: '+60', name: 'Malaysia', flag: '🇲🇾', searchTerms: 'malaysia my +60 60' },
  { code: 'TH', dialCode: '+66', name: 'Thailand', flag: '🇹🇭', searchTerms: 'thailand th +66 66' },
  { code: 'ID', dialCode: '+62', name: 'Indonesia', flag: '🇮🇩', searchTerms: 'indonesia id +62 62' },
  { code: 'PH', dialCode: '+63', name: 'Philippines', flag: '🇵🇭', searchTerms: 'philippines ph +63 63' },
  { code: 'VN', dialCode: '+84', name: 'Vietnam', flag: '🇻🇳', searchTerms: 'vietnam vn +84 84' },
  { code: 'BD', dialCode: '+880', name: 'Bangladesh', flag: '🇧🇩', searchTerms: 'bangladesh bd +880 880' },
  { code: 'PK', dialCode: '+92', name: 'Pakistan', flag: '🇵🇰', searchTerms: 'pakistan pk +92 92' },
  { code: 'LK', dialCode: '+94', name: 'Sri Lanka', flag: '🇱🇰', searchTerms: 'sri lanka lk +94 94' },
  { code: 'NP', dialCode: '+977', name: 'Nepal', flag: '🇳🇵', searchTerms: 'nepal np +977 977' },
  { code: 'IL', dialCode: '+972', name: 'Israel', flag: '🇮🇱', searchTerms: 'israel il +972 972' },
  { code: 'TR', dialCode: '+90', name: 'Turkey', flag: '🇹🇷', searchTerms: 'turkey tr türkiye +90 90' },
  { code: 'EG', dialCode: '+20', name: 'Egypt', flag: '🇪🇬', searchTerms: 'egypt eg +20 20' },
  { code: 'NG', dialCode: '+234', name: 'Nigeria', flag: '🇳🇬', searchTerms: 'nigeria ng +234 234' },
  { code: 'KE', dialCode: '+254', name: 'Kenya', flag: '🇰🇪', searchTerms: 'kenya ke +254 254' },
  { code: 'NL', dialCode: '+31', name: 'Netherlands', flag: '🇳🇱', searchTerms: 'netherlands nl holland +31 31' },
  { code: 'BE', dialCode: '+32', name: 'Belgium', flag: '🇧🇪', searchTerms: 'belgium be +32 32' },
  { code: 'SE', dialCode: '+46', name: 'Sweden', flag: '🇸🇪', searchTerms: 'sweden se +46 46' },
  { code: 'NO', dialCode: '+47', name: 'Norway', flag: '🇳🇴', searchTerms: 'norway no +47 47' },
  { code: 'DK', dialCode: '+45', name: 'Denmark', flag: '🇩🇰', searchTerms: 'denmark dk +45 45' },
  { code: 'FI', dialCode: '+358', name: 'Finland', flag: '🇫🇮', searchTerms: 'finland fi +358 358' },
  { code: 'PL', dialCode: '+48', name: 'Poland', flag: '🇵🇱', searchTerms: 'poland pl +48 48' },
  { code: 'AT', dialCode: '+43', name: 'Austria', flag: '🇦🇹', searchTerms: 'austria at +43 43' },
  { code: 'CH', dialCode: '+41', name: 'Switzerland', flag: '🇨🇭', searchTerms: 'switzerland ch +41 41' },
  { code: 'PT', dialCode: '+351', name: 'Portugal', flag: '🇵🇹', searchTerms: 'portugal pt +351 351' },
  { code: 'GR', dialCode: '+30', name: 'Greece', flag: '🇬🇷', searchTerms: 'greece gr +30 30' },
  { code: 'IE', dialCode: '+353', name: 'Ireland', flag: '🇮🇪', searchTerms: 'ireland ie +353 353' },
  { code: 'HK', dialCode: '+852', name: 'Hong Kong', flag: '🇭🇰', searchTerms: 'hong kong hk +852 852' },
  { code: 'TW', dialCode: '+886', name: 'Taiwan', flag: '🇹🇼', searchTerms: 'taiwan tw +886 886' },
];

/**
 * Search country codes by term.
 */
export function searchCountryCodes(term: string): CountryCode[] {
  if (!term || term.trim() === '') {
    return COUNTRY_CODES;
  }
  
  const searchTerm = term.toLowerCase().trim();
  
  return COUNTRY_CODES.filter(country => 
    country.searchTerms.includes(searchTerm) ||
    country.name.toLowerCase().includes(searchTerm) ||
    country.code.toLowerCase().includes(searchTerm) ||
    country.dialCode.includes(searchTerm)
  );
}

/**
 * Get country by dial code.
 */
export function getCountryByDialCode(dialCode: string): CountryCode | undefined {
  const normalized = normalizeCountryCode(dialCode);
  return COUNTRY_CODES.find(c => c.dialCode === normalized);
}

/**
 * Get country by country code.
 */
export function getCountryByCode(code: string): CountryCode | undefined {
  return COUNTRY_CODES.find(c => c.code === code.toUpperCase());
}
